import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/store/auth.store";
import Tabbar from "@/components/fragments/Tabbar";
import { useTransactionStore } from "@/features/Cart/store/transactionStore";
import { useShallow } from "zustand/shallow";

interface BeforeInstallPromptEvent extends Event {
  prompt(): Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed" }>;
}

export default function SettingsPage() {
  const [showModal, setShowModal] = useState(false);
  const [showThemeModal, setShowThemeModal] = useState(false);
  const [selectedTheme, setSelectedTheme] = useState<
    "light" | "dark" | "system"
  >("system");
  const logout = useAuthStore((state) => state.logout);
  const navigate = useNavigate();

  const [clearTransaction, clearCart] = useTransactionStore(
    useShallow((state) => [state.clearTransaction, state.clearCart]),
  );

  const [deferredPrompt, setDeferredPrompt] =
    useState<BeforeInstallPromptEvent | null>(null);
  const [isIOS, setIsIOS] = useState(false);
  const [isStandalone, setIsStandalone] = useState(false);
  const [isAndroid, setIsAndroid] = useState(false);

  useEffect(() => {
    // Load theme dari localStorage saat pertama kali mount
    const savedTheme = localStorage.getItem("theme");
    if (savedTheme === "light" || savedTheme === "dark") {
      setSelectedTheme(savedTheme);
    } else {
      setSelectedTheme("system");
    }

    // Deteksi iOS
    const iOS =
      /iPad|iPhone|iPod/.test(navigator.userAgent) && !(window as any).MSStream;
    setIsIOS(iOS);

    // Deteksi Android
    const android = /Android/i.test(navigator.userAgent);
    setIsAndroid(android);

    // Deteksi apakah sudah terinstall (standalone mode)
    const standalone =
      window.matchMedia("(display-mode: standalone)").matches ||
      (window.navigator as any).standalone ||
      document.referrer.includes("android-app://");
    setIsStandalone(standalone);

    // Handler untuk Android/Chrome
    const handler = (e: Event) => {
      // console.log("✅ beforeinstallprompt event fired!");
      e.preventDefault();
      setDeferredPrompt(e as BeforeInstallPromptEvent);
    };

    window.addEventListener("beforeinstallprompt", handler);

    // Log untuk debugging
    console.log("PWA Debug Info:", {
      isHTTPS: window.location.protocol === "https:",
      isLocalhost: window.location.hostname === "localhost",
      userAgent: navigator.userAgent,
      isIOS: iOS,
      isAndroid: android,
      isStandalone: standalone,
      protocol: window.location.protocol,
    });

    return () => {
      window.removeEventListener("beforeinstallprompt", handler);
    };
  }, []);

  const handleThemeChange = (theme: "light" | "dark" | "system") => {
    setSelectedTheme(theme);

    if (theme === "light") {
      localStorage.setItem("theme", "light");
      document.documentElement.classList.remove("dark");
    } else if (theme === "dark") {
      localStorage.setItem("theme", "dark");
      document.documentElement.classList.add("dark");
    } else {
      localStorage.removeItem("theme");
      document.documentElement.classList.toggle(
        "dark",
        window.matchMedia("(prefers-color-scheme: dark)").matches,
      );
    }
  };

  const handleSaveTheme = () => {
    setShowThemeModal(false);
  };

  const handleLogout = () => {
    clearTransaction();
    clearCart();
    logout();
    localStorage.removeItem("transaction");
    localStorage.removeItem("auth-storage");
    navigate("/auth/login", { replace: true });
  };

  const handleInstallApp = async () => {
    console.log("Install button clicked", {
      isStandalone,
      isIOS,
      isAndroid,
      hasDeferredPrompt: !!deferredPrompt,
      protocol: window.location.protocol,
    });

    if (isStandalone) {
      alert("Aplikasi sudah terinstall di perangkat Anda!");
      return;
    }

    if (isIOS) {
      showIOSInstallInstructions();
      return;
    }

    if (deferredPrompt) {
      try {
        await deferredPrompt.prompt();
        const { outcome } = await deferredPrompt.userChoice;

        if (outcome === "accepted") {
          console.log("✅ User menerima instalasi");
          alert("Aplikasi berhasil diinstall!");
        } else {
          console.log("❌ User menolak instalasi");
        }

        setDeferredPrompt(null);
      } catch (error) {
        console.error("Error saat instalasi:", error);
        alert("Terjadi kesalahan saat instalasi. Silakan coba lagi.");
      }
    } else {
      if (isAndroid) {
        showAndroidManualInstallInstructions();
      } else {
        alert(
          "Instalasi tidak tersedia saat ini. Pastikan Anda membuka aplikasi dari browser yang mendukung PWA (Chrome, Edge, atau Samsung Internet).",
        );
      }
    }
  };

  const showIOSInstallInstructions = () => {
    const message = `
Untuk menginstall aplikasi ini di iOS:

1. Tap tombol Share (ikon kotak dengan panah ke atas) di bagian bawah browser Safari
2. Scroll ke bawah dan pilih "Add to Home Screen" atau "Tambahkan ke Layar Utama"
3. Tap "Add" atau "Tambah" di pojok kanan atas

Aplikasi akan muncul di home screen Anda!
    `.trim();

    alert(message);
  };

  const showAndroidManualInstallInstructions = () => {
    const isHTTPS = window.location.protocol === "https:";

    if (!isHTTPS) {
      alert(
        `
⚠️ Untuk menginstall aplikasi ini, Anda perlu:

1. Mengakses aplikasi via HTTPS (saat ini Anda menggunakan HTTP)
2. Atau deploy aplikasi ke hosting dengan HTTPS

Saat ini Anda dalam mode development/preview. Untuk testing PWA install:
• Deploy ke Vercel/Netlify (gratis & otomatis HTTPS)
• Atau gunakan ngrok untuk HTTPS tunnel

Setelah menggunakan HTTPS, di Chrome/Edge:
• Tap menu (3 titik) > "Install app" atau "Tambahkan ke layar utama"
      `.trim(),
      );
    } else {
      alert(
        `
Untuk menginstall aplikasi ini di Android:

1. Di Chrome: Tap menu (3 titik vertikal) di pojok kanan atas
2. Pilih "Install app" atau "Tambahkan ke layar utama"
3. Konfirmasi instalasi

Atau:
• Di Chrome: Lihat banner "Add to Home Screen" yang muncul otomatis
• Di Edge: Menu > "Apps" > "Install this site as an app"
      `.trim(),
      );
    }
  };

  const getThemeLabel = () => {
    if (selectedTheme === "light") return "Mode Terang";
    if (selectedTheme === "dark") return "Mode Gelap";
    return "Ikuti Sistem";
  };

  const canInstall = !isStandalone && (isIOS || isAndroid);

  return (
    <div className="relative min-h-screen">
      <Tabbar />
      <main className="flex-1 px-4 pt-6">
        <div className="max-w-4xl mx-auto space-y-3">
          <div className="bg-white rounded-2xl overflow-hidden border border-[#efecff] dark:bg-transparent ">
            <a
              href="https://sellerku.id/#faqs"
              target="_blank"
              rel="noopener noreferrer"
              className="cursor-pointer"
            >
              <button className="w-full px-5 py-4 text-left flex items-center justify-between hover:bg-[#f9f8fd] dark:hover:bg-[#1b1b1b] transition-colors border-b border-[#efecff]">
                <div>
                  <p className="font-medium text-[#1e1e1e] dark:text-white">
                    Pusat Bantuan
                  </p>
                  <p className="text-sm text-gray-600 mt-0.5 dark:text-white">
                    FAQ, hubungi support, panduan
                  </p>
                </div>
                <span className="text-[#d7d0fe] text-xl dark:text-white">
                  ›
                </span>
              </button>
            </a>

            <button className="w-full px-5 py-4 text-left flex items-center justify-between hover:bg-[#f9f8fd] dark:hover:bg-[#1b1b1b] transition-colors border-b border-[#efecff]">
              <div>
                <p className="font-medium text-[#1e1e1e]  dark:text-white">
                  Versi Aplikasi
                </p>
                <p className="text-sm text-gray-600 mt-0.5 dark:text-white">
                  Versi 1.0.0 • Terbaru
                </p>
              </div>
              <span className="text-[#d7d0fe] text-xl">›</span>
            </button>

            {/* Button Ubah Tema */}
            <button
              onClick={() => setShowThemeModal(true)}
              className="w-full px-5 py-4 text-left flex items-center justify-between hover:bg-[#f9f8fd] dark:hover:bg-[#1b1b1b] transition-colors border-b border-[#efecff]"
            >
              <div>
                <p className="font-medium text-[#1e1e1e] dark:text-white">
                  Ubah Tema
                </p>
                <p className="text-sm text-gray-600 mt-0.5 dark:text-white">
                  {getThemeLabel()}
                </p>
              </div>
              <span className="text-[#d7d0fe] text-xl dark:text-white">›</span>
            </button>

            {/* Button Install App */}
            <button
              onClick={handleInstallApp}
              className="w-full px-5 py-4 text-left flex items-center justify-between hover:bg-[#f9f8fd] dark:hover:bg-[#1b1b1b] transition-colors"
            >
              <div>
                <p className="font-medium text-[#1e1e1e] dark:text-white">
                  Install App ke Layar Utama
                </p>
                <p className="text-sm text-gray-600 mt-0.5 dark:text-white">
                  {isIOS
                    ? "Lihat cara install di iOS"
                    : deferredPrompt
                      ? "Tambahkan ke home screen untuk akses cepat"
                      : "Lihat cara install manual"}
                </p>
              </div>
              <span className="text-[#d7d0fe] text-xl dark:text-white">›</span>
            </button>
          </div>

          <div className="pt-4">
            <button
              onClick={() => setShowModal(true)}
              className="w-full bg-white dark:bg-[#37393d] dark:border-[#37393d] dark:text-red-400 text-red-600 font-medium py-4 rounded-2xl border border-red-100 hover:bg-red-50 transition-colors"
            >
              Keluar dari Akun
            </button>
          </div>

          <p className="mt-6 text-xs text-center text-gray-500 dark:text-white/80">
            © 2025 Kasir App • Semua hak cipta dilindungi
          </p>
        </div>
      </main>

      {/* Modal Logout */}
      {showModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[999]">
          <div className="bg-white dark:bg-[#37393d] rounded-xl p-6 w-80 text-center space-y-4">
            <p className="text-lg font-medium text-gray-900 dark:text-white">
              Apakah Anda yakin ingin keluar?
            </p>
            <div className="flex justify-between gap-4">
              <button
                onClick={() => setShowModal(false)}
                className="flex-1 py-2 transition-colors border border-gray-300 rounded-lg dark:text-white hover:bg-gray-100 dark:hover:text-black"
              >
                Batal
              </button>
              <button
                onClick={handleLogout}
                className="flex-1 py-2 text-white transition-colors bg-red-600 rounded-lg hover:bg-red-700"
              >
                Keluar
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Modal Theme */}
      {showThemeModal && (
        <div className="fixed inset-0 bg-black/30 flex items-center justify-center z-[999]">
          <div className="bg-white dark:bg-[#37393d] rounded-xl p-6 w-80 space-y-4">
            <p className="text-lg font-medium text-center text-gray-900 dark:text-white">
              Pilih Tema
            </p>

            <div className="space-y-3">
              {/* Light Mode */}
              <label className="flex items-center p-3 transition-colors border border-gray-200 rounded-lg cursor-pointer dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                <input
                  type="radio"
                  name="theme"
                  value="light"
                  checked={selectedTheme === "light"}
                  onChange={(e) => handleThemeChange(e.target.value as "light")}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="ml-3 text-gray-900 dark:text-white">
                  Mode Terang
                </span>
              </label>

              {/* Dark Mode */}
              <label className="flex items-center p-3 transition-colors border border-gray-200 rounded-lg cursor-pointer dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                <input
                  type="radio"
                  name="theme"
                  value="dark"
                  checked={selectedTheme === "dark"}
                  onChange={(e) => handleThemeChange(e.target.value as "dark")}
                  className="w-4 h-4 text-blue-600"
                />
                <span className="ml-3 text-gray-900 dark:text-white">
                  Mode Gelap
                </span>
              </label>

              {/* System Mode */}
              <label className="flex items-center p-3 transition-colors border border-gray-200 rounded-lg cursor-pointer dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700">
                <input
                  type="radio"
                  name="theme"
                  value="system"
                  checked={selectedTheme === "system"}
                  onChange={(e) =>
                    handleThemeChange(e.target.value as "system")
                  }
                  className="w-4 h-4 text-blue-600"
                />
                <span className="ml-3 text-gray-900 dark:text-white">
                  Ikuti Sistem
                </span>
              </label>
            </div>

            <div className="flex justify-between gap-4 pt-2">
              <button
                onClick={() => setShowThemeModal(false)}
                className="flex-1 py-2 transition-colors border border-gray-300 rounded-lg dark:text-white hover:bg-gray-100 dark:hover:text-black"
              >
                Batal
              </button>
              <button
                onClick={handleSaveTheme}
                className="flex-1 py-2 text-white transition-colors bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Simpan
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="h-6 bg-transparent" />
    </div>
  );
}
